// SearchModal.js
import React, { useState, useEffect, useMemo } from "react"
import { Modal, Box, IconButton, CircularProgress, TextField, TablePagination } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import { MaterialReactTable, useMaterialReactTable } from "material-react-table"
import { useQuery } from "@tanstack/react-query"
import fetchwrapper from "../../../services/interceptors/fetchwrapper"
import { MRT_Localization_ES } from "material-react-table/locales/es"
const style = {
  Modal: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "98%",
    minHeight: "98%",
    maxHeight: "98%",
    bgcolor: "background.paper",
    border: "2px solid #000",
    boxShadow: 24,
    p: 4,
    overflowY: "auto",
  },

  CloseIcon: {
    cursor: "pointer",
    marginLeft: "95%",
  },
}
const CustomTable = ({
  data,
  columnsTable,
  perPage,
  isLoading,
  isError,
  totalPages,
  currentPage,
  onPageChange,
  onFilterChange,
  onItemSelected,
  handleCloseModal,
  errorMsgFilterSearch,
}) => {
  const columns = useMemo(
    () =>
      columnsTable.map((column) => ({
        ...column,
        Filter: ({ column }) => (
          <TextField
            variant="standard"
            placeholder="Filtrar"
            onChange={(e) => onFilterChange(column.id, e.target.value)}
          />
        ),
      })),
    [onFilterChange],
  )

  const table = useMaterialReactTable({
    columns,
    data,
    initialState: { columnVisibility: { ciacodigo: false }, showColumnFilters: true },
    enableEditing: false,
    enableSorting: false,
    enablePagination: false,
    enableRowActions: false,
    enableTopToolbar: false,
    enableColumnResizing: true,
    enableSelectAll: false,
    enableRowSelection: true,
    enableMultiRowSelection: false,
    enableColumnFilters: true,
    localization: { ...MRT_Localization_ES },
    muiToolbarAlertBannerProps: isError ? { color: "error", children: errorMsgFilterSearch } : undefined,
    muiTableContainerProps: {
      sx: {
        minWidth: "100%",
        maxWidth: "100%",
        maxHeight: "600px", // Ajusta la altura máxima de la tabla
        overflowY: "auto",
        overflowX: "auto",
      },
    },
    muiTableBodyCellProps: {
      sx: {
        py: 0.4, // Ajusta la altura de las celdas
        fontSize: "0.875rem", // Ajusta el tamaño de la fuente
      },
    },
    muiTableHeadCellProps: {
      sx: {
        fontSize: "0.875rem",
        fontWeight: "bold",
      },
    },
    state: {
      isLoading,
    },
  })

  useEffect(() => {
    if (table.getSelectedRowModel().rows[0] != null) {
      onItemSelected(table.getSelectedRowModel().rows[0].original)
      handleCloseModal()
    }
  }, [table.getSelectedRowModel()])

  return (
    <>
      <MaterialReactTable table={table} />
      <TablePagination
        rowsPerPageOptions={[perPage]}
        component="div"
        count={totalPages * perPage}
        rowsPerPage={perPage}
        page={currentPage - 1}
        onPageChange={(_, newPage) => onPageChange(newPage + 1)}
      />
    </>
  )
}
const SearchModal = ({
  endpoint,
  openModal,
  handleCloseModal,
  queryKey,
  perPage,
  onItemSelected,
  columnsTable,
  errorMsg,
}) => {
  const [filters, setFilters] = useState({})
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  // Fetch data with filters and pagination
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [queryKey, page, filters],
    queryFn: async () => {
      const response = await fetchwrapper(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          typeSearch: "filter",
          page,
          perPage,
          filters,
        }),
      })
      const result = await response.json()
      setTotalPages(result.total_pages)
      return result.data
    },
    keepPreviousData: false,
  })

  // Handle filter changes
  const handleFilterChange = (columnId, value) => {
    setFilters((prevFilters) => ({ ...prevFilters, [columnId]: value }))
  }
  useEffect(() => {
    refetch()
  }, [page, filters, refetch])

  // refresca tabla solo cuando se abre por primera vez el modal y cuando se cierre setear todo por defecto
  useEffect(() => {
    if (openModal) {
      refetch()
    } else {
      setFilters({})
      setPage(1)
    }
  }, [openModal, refetch])

  return (
    <Modal
      open={openModal}
      onClose={handleCloseModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style.Modal}>
        <IconButton onClick={handleCloseModal} style={style.CloseIcon}>
          <CloseIcon />
        </IconButton>
        <CustomTable
          data={data || []}
          columnsTable={columnsTable}
          perPage={perPage}
          isLoading={isLoading}
          isError={isError}
          totalPages={totalPages}
          currentPage={page}
          onPageChange={setPage}
          onFilterChange={handleFilterChange}
          onItemSelected={(item) => {
            onItemSelected(item)
            handleCloseModal()
          }}
          handleCloseModal={handleCloseModal}
          errorMsgFilterSearch={errorMsg}
        />
        {isLoading && <CircularProgress />}
      </Box>
    </Modal>
  )
}

export default SearchModal
